import { useMemo, useState } from 'react'
import { Search, Users } from 'lucide-react'
import Checkbox from './Checkbox'
import Meter from './Meter'
import { kit as K } from './strings'
import './GroupPicker.css'

/**
 * A checkbox list of departments, with select-all and a filter.
 *
 * Four legacy forms assign something to departments — the campaign, the
 * bulletin, the survey and the policy — and each does it with a select2 whose
 * "select all" is a sentinel OPTION. An option that is not a value is a trap:
 * it shows as a chosen item and posts nothing. Here select-all is a control,
 * and the filter narrows the list without changing what is selected.
 *
 * BUILT FOR A DIRECTORY, not a handful. An installation can carry thousands of
 * departments, which rules out listing the chosen ones back: a selection of four
 * hundred is not something to render as chips, and at that size the only summary
 * worth having is how many of how many. Only a window of matches goes into the
 * DOM, with the rest named rather than silently dropped.
 */
const WINDOW = 200

export default function GroupPicker({
  options = [],
  selected = [],
  onChange,
  legend,
  error,
  selectAllLabel,
  filterLabel,
  emptyLabel = K.pickerEmpty,
  moreLabel = K.pickerMore,
  /* Not rendered — the meter is a progressbar, and a progressbar with no name is
     unreadable to anything that is not looking at it. */
  tallyLabel = K.pickerChosen,
}) {
  const [query, setQuery] = useState('')

  /* A Set, not `includes`: at four thousand departments the linear scan runs
     once per row on every keystroke. */
  const chosen = useMemo(() => new Set(selected), [selected])

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return options
    return options.filter((option) => String(option.label).toLowerCase().includes(needle))
  }, [options, query])

  const shown = matches.length > WINDOW ? matches.slice(0, WINDOW) : matches

  /* Select-all applies to what is MATCHED, not to what is on screen: the window
     is a rendering limit, and "all" meaning the first two hundred would be a
     quiet lie. With a filter up it is still the filtered set, not the directory. */
  const allMatchedSelected = matches.length > 0 && matches.every((o) => chosen.has(o.value))

  const toggleAll = () => {
    const ids = matches.map((option) => option.value)
    onChange(
      allMatchedSelected
        ? selected.filter((id) => !ids.includes(id))
        : [...new Set([...selected, ...ids])],
    )
  }

  const toggle = (id) =>
    onChange(chosen.has(id) ? selected.filter((item) => item !== id) : [...selected, id])

  const count = (value) => value.toLocaleString('en-US')

  return (
    <fieldset className="group-picker">
      <legend className="group-picker__legend">
        <bdi>{legend}</bdi>
        {error && <span className="group-picker__error">{error}</span>}
      </legend>

      <div className={`group-picker__box${error ? ' is-invalid' : ''}`}>
        <div className="group-picker__head">
          {/* THE SUMMARY, and at this scale the only one worth having. A figure
              against its total says little on its own — four hundred of how many
              is the question — so the proportion is drawn under it. */}
          <div className="group-picker__tally">
            <span className="group-picker__tally-figures">
              <strong className="group-picker__tally-count tabular">{count(selected.length)}</strong>
              <span className="group-picker__tally-of tabular">/ {count(options.length)}</span>
            </span>
            <Meter
              className="group-picker__tally-meter"
              value={options.length ? (selected.length / options.length) * 100 : 0}
              size="sm"
              label={tallyLabel}
            />
          </div>

          <span className="group-picker__search">
            <Search size={14} strokeWidth={1.75} aria-hidden="true" />
            <input
              type="search"
              value={query}
              placeholder={filterLabel}
              aria-label={filterLabel}
              onChange={(event) => setQuery(event.target.value)}
            />
          </span>
        </div>

        <div className="group-picker__all">
          <Checkbox checked={allMatchedSelected} onChange={toggleAll}>
            {selectAllLabel}
          </Checkbox>
        </div>

        {shown.length === 0 ? (
          <p className="group-picker__empty">
            <Users size={16} strokeWidth={1.75} aria-hidden="true" />
            <bdi>{emptyLabel}</bdi>
          </p>
        ) : (
          <>
            <div className="group-picker__list">
              {shown.map((option) => {
                const on = chosen.has(option.value)
                return (
                  <span key={option.value} className={`group-picker__item${on ? ' is-on' : ''}`}>
                    <Checkbox checked={on} onChange={() => toggle(option.value)}>
                      <bdi>{option.label}</bdi>
                    </Checkbox>
                  </span>
                )
              })}
            </div>

            {/* Named, not dropped: a list that quietly stops at two hundred looks
                like a directory that ends there. */}
            {matches.length > shown.length && (
              <p className="group-picker__more">
                {moreLabel(count(shown.length), count(matches.length))}
              </p>
            )}
          </>
        )}
      </div>
    </fieldset>
  )
}
